import { useState, useEffect } from 'react';
import { ViewState, PerformanceData } from '../types';
import { performanceDataEmitter } from '../emitters';

export const usePerformanceData = () => {
  const [requests, setRequests] = useState<PerformanceData[]>([]);
  const [viewState, setViewState] = useState<ViewState>({ mode: 'collapsed' });
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const unsubscribePerformanceData = performanceDataEmitter.subscribe((performanceData) => {
      updateRequests(performanceData);
    });

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleGoBack();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      unsubscribePerformanceData();
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const updateRequests = (performanceData: PerformanceData) => {
    const now = Date.now();
    const idPrefix = `${performanceData.miniProfiler?.name} Query`;
    const root = performanceData?.miniProfiler?.data?.root;
    const child = root?.children?.[0];
    const sqlDuration =
      child?.sql_timings_duration_milliseconds ??
      performanceData?.miniProfiler?.data?.duration_milliseconds_in_sql ??
      0;

    const totalDuration =
      child?.duration_milliseconds ?? performanceData?.miniProfiler?.data?.duration_milliseconds ?? 0;

    setRequests((prev) => {
      const newRequest: PerformanceData = {
        id: `req-${now}`,
        name: idPrefix,
        timestamp: now,
        bulletWarnings: performanceData.bulletWarnings,
        miniProfiler: performanceData.miniProfiler,
        memoryProfiler: performanceData.memoryProfiler,
        durationTotal: totalDuration,
        durationSql: sqlDuration
      };

      return [...prev, newRequest];
    });
  };

  const handleGoBack = () => {
    setViewState((prev) => {
      if (['sql', 'bullet', 'memory'].includes(prev.mode)) {
        return { mode: 'requests', selectedRequestId: prev.selectedRequestId };
      } else if (prev.mode === 'requests') {
        return { mode: 'collapsed' };
      }
      return prev;
    });
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  const getSelectedRequest = () => {
    if (!viewState.selectedRequestId) return null;
    return requests.find((r) => r.id === viewState.selectedRequestId);
  };

  const handleSelectRequest = (mode: 'sql' | 'bullet' | 'memory', requestId: string) => {
    setViewState({
      mode,
      selectedRequestId: requestId,
      ...(mode === 'sql' ? { selectedSqlId: 0 } : {})
    });
  };

  return {
    requests,
    viewState,
    isVisible,
    setViewState,
    handleGoBack,
    handleClose,
    getSelectedRequest,
    handleSelectRequest
  };
};
